import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../../services/api";
import { useForm } from "react-hook-form";
import { Loader2, ArrowLeft } from "lucide-react";
import toast from "react-hot-toast";
import Errors from "../Errors";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useTranslation } from "react-i18next";

const UserDetails = () => {
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: { username: "", email: "", role: "", enabled: "", password: "" },
    mode: "onSubmit",
  });

  const [loading, setLoading] = useState(false);
  const { userId } = useParams();
  const [user, setUser] = useState(null);
  const [error, setError] = useState(null);

  const fetchUserDetails = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get(`/admin/users/${userId}`);
      setUser(response.data);
      setValue("username", response.data.username);
      setValue("email", response.data.email);
      setValue("role", response.data.role?.roleName || "");
      setValue("enabled", response.data.enabled);
    } catch (err) {
      setError(err?.response?.data?.message);
    } finally {
      setLoading(false);
    }
  }, [userId, setValue]);

  useEffect(() => {
    fetchUserDetails();
  }, [fetchUserDetails]);

  useEffect(() => {
    if (user && Object.keys(user).length > 0) {
      setValue("username", user.username);
      setValue("email", user.email);
      setValue("role", user.role);
      setValue("enabled", user.enabled);
    }
  }, [user, setValue]);

  const handleSaveUser = async (data) => {
    try {
      await api.put(`/admin/users/update`, {
        id: userId,
        username: data.username,
        email: data.email,
        role: data.role,
        enabled: data.enabled,
        password: data.password,
      });
      setValue("password", "");
      toast.success(t("userDetails_saveSuccess"));
    } catch (err) {
      toast.error(t("userDetails_saveError"));
    }
  };

  if (error) return <Errors message={error} />;

  return (
    <div className="min-h-[calc(100vh-64px)] px-4 py-12">
      <div className="max-w-xl mx-auto">

        {/* Header */}
        <div className="mb-8">
          <Link to="/admin/users" className="inline-flex items-center gap-1.5 text-xs text-zinc-500 hover:text-zinc-300 transition-colors mb-4">
            <ArrowLeft size={14} />
            {t("userDetails_backLink")}
          </Link>
          <span className="block text-xs font-mono uppercase tracking-widest text-violet-400">{t("userDetails_label")}</span>
          <h1 className="text-2xl font-bold text-white mt-1">{t("userDetails_title")}</h1>
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center h-48 gap-3">
            <Loader2 size={28} className="animate-spin text-violet-400" />
            <span className="text-sm text-zinc-500">{t("userDetails_loading")}</span>
          </div>
        ) : (
          <div className="rounded-xl border border-white/8 bg-white/2 p-6">
            <form onSubmit={handleSubmit(handleSaveUser)} className="space-y-4">

              <div className="space-y-1.5">
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">{t("userDetails_usernameLabel")}</Label>
                <Input
                  {...register("username")}
                  className="bg-white/5 border-white/10 text-white focus-visible:ring-violet-500/50 focus-visible:border-violet-500/50"
                />
              </div>

              <div className="space-y-1.5">
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">{t("userDetails_emailLabel")}</Label>
                <Input
                  {...register("email")}
                  className="bg-white/5 border-white/10 text-white focus-visible:ring-violet-500/50 focus-visible:border-violet-500/50"
                />
              </div>

              <div className="space-y-1.5">
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">{t("userDetails_roleLabel")}</Label>

                <select
                  {...register("role")}
                  className="w-full px-3 py-2 rounded-md bg-zinc-900 border border-white/10 text-white"
                >
                  <option value="USER">{t("userDetails_roleUser")}</option>
                  <option value="ADMIN">{t("userDetails_roleAdmin")}</option>
                </select>
              </div>

              <div className="space-y-1.5">
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">{t("userDetails_enabledLabel")}</Label>

                <select
                  {...register("enabled")}
                  className="w-full px-3 py-2 rounded-md bg-zinc-900 border border-white/10 text-white"
                >
                  <option value="true">{t("userDetails_enabledTrue")}</option>
                  <option value="false">{t("userDetails_enabledFalse")}</option>
                </select>
              </div>

              <div className="space-y-1.5">
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">{t("userDetails_passwordLabel")}</Label>
                <Input
                  type="password"
                  placeholder={t("userDetails_passwordPlaceholder")}
                  {...register("password", { minLength: 6 })}
                  readOnly
                  className="bg-white/5 border-white/10 text-white placeholder:text-zinc-600 focus-visible:ring-violet-500/50 focus-visible:border-violet-500/50 cursor-not-allowed"
                />
              </div>

              <Separator className="bg-white/8 my-2" />

              <Button
                type="submit"
                className="w-full bg-violet-600 hover:bg-violet-500 text-white font-semibold"
              >
                {t("userDetails_saveButton")}
              </Button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserDetails;
